import React, { memo } from "react";
import { useTranslation } from "react-i18next";

import { getLegalDocumentMetadata } from "../../utils/legal/legalStatus";

function LegalList({ items }) {
  return (
    <ul className="list-disc pl-6 space-y-1.5 marker:text-primary">
      {items.map((item, index) => {
        if (typeof item === "string") {
          return <li key={index}>{item}</li>;
        }

        return (
          <li key={index}>
            {item.text}
            {Array.isArray(item.list) && item.list.length > 0 ? (
              <LegalList items={item.list} />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function LegalBlock({ block }) {
  if (block.heading) {
    return <h2 className="subtitle-text text-typography mt-4">{block.heading}</h2>;
  }

  if (Array.isArray(block.list)) {
    return <LegalList items={block.list} />;
  }

  if (block.text) {
    return <p className="leading-relaxed">{block.text}</p>;
  }

  return null;
}

function LegalDocumentPage({ documentType }) {
  const { t } = useTranslation(["common", "legal"]);
  const document = getLegalDocumentMetadata(t, documentType);
  const body = Array.isArray(document.body) ? document.body : [];
  const hasContent = body.length > 0;

  return (
    <div className="w-full max-w-5xl min-h-full p-4 md:p-6 flex flex-col gap-4">
      <header className="flex flex-col gap-2">
        <h1 className="title-text">{document.title}</h1>
        <div className="flex flex-wrap gap-4 text-sm text-greyscale-2">
          <p>
            <span className="font-semibold">
              {t("labelling.version", { defaultValue: "Version" })}:
            </span>{" "}
            {document.version}
          </p>
          <p>
            <span className="font-semibold">{document.effectiveDateLabel}:</span>{" "}
            {document.effectiveDateText}
          </p>
        </div>
      </header>

      {hasContent ? (
        <article className="flex flex-col gap-3 text-typography leading-relaxed">
          {body.map((block, index) => (
            <LegalBlock key={index} block={block} />
          ))}
        </article>
      ) : (
        <div className="flex flex-col gap-1 text-sm text-greyscale-2">
          <p className="font-semibold text-greyscale-2">
            {t("page.unavailableTitle", {
              ns: "legal",
              defaultValue: "Document unavailable",
            })}
          </p>
          <p>
            {t("page.unavailableBody", {
              ns: "legal",
              defaultValue:
                "This legal document is not available for your current locale right now.",
            })}
          </p>
        </div>
      )}
    </div>
  );
}

export default memo(LegalDocumentPage);
